/**
 * Gym Locations Service
 * 
 * REQUIREMENTS:
 * - Provides functions to interact with gym locations in the database
 * - Auto-syncs venue_geofences when a gym location is created or updated
 *   so that mobile app check-in can verify athlete proximity to the gym
 */

import { supabase } from '../supabase/client';
import { withOrganizationScope } from '@/utils/supabase/organizationScope';

import { createLogger } from '@/utils/logger/devLogger';

const logger = createLogger('gymLocationsService');

/**
 * Sync geofence for a gym location (fire-and-forget, non-blocking).
 * Calls the /api/geofences/sync endpoint to geocode the address and create/update
 * a venue_geofences record so mobile check-in can verify athlete proximity.
 */
async function syncLocationGeofence(location: { name: string; address?: string | null; city?: string | null; state?: string | null; zip?: string | null; organization_id?: string; previousName?: string }): Promise<void> {
  try {
    if (!location.address || !location.organization_id || !location.name) {
      logger.debug('Skipping geofence sync — missing address, name, or organization_id');
      return;
    }

    const response = await fetch('/api/geofences/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: location.name,
        address: location.address,
        city: location.city || undefined,
        state: location.state || undefined,
        zip: location.zip || undefined,
        organizationId: location.organization_id,
        previousName: location.previousName || undefined,
      }),
    });

    const result = await response.json();

    if (result.success) {
      logger.info('Geofence synced for gym location:', { name: location.name, geofenceId: result.geofenceId });
    } else {
      logger.warn('Geofence sync skipped or failed (non-blocking):', { name: location.name, error: result.error });
    }
  } catch (error) {
    // Non-blocking — geofence sync failure should never prevent location save
    logger.warn('Geofence sync request failed (non-blocking):', error);
  }
}

export interface OperatingHoursDay {
  day: number; // 0-6
  open: string; // HH:MM
  close: string; // HH:MM
  is_closed: boolean;
}

export interface GymLocation {
  id: string;
  name: string;
  address: string;
  address_line_2?: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email?: string;
  billing_contact_email?: string;
  billing_contact_phone?: string;
  reply_to_email?: string;
  timezone: string; // Required timezone field (IANA timezone identifier)
  operating_hours?: OperatingHoursDay[]; // Weekly building hours
  created_at?: string;
  updated_at?: string;
  organization_id?: string;
}

export type GymLocationInput = Omit<GymLocation, 'id' | 'created_at' | 'updated_at'> & {
  operating_hours?: OperatingHoursDay[];
};

const TABLE_NAME = 'gymlocations';

/**
 * Get all gym locations
 * If organizationId is provided, locations will be filtered by that organization
 */
export const getAllLocations = async (organizationId?: string): Promise<GymLocation[]> => {
  try {
    logger.info(`Getting all gym locations${organizationId ? ` for organization ${organizationId}` : ''}...`);
    
    let query = supabase
      .from(TABLE_NAME)
      .select('*');
    
    // Apply organization scope if provided
    if (organizationId) {
      query = withOrganizationScope(query, organizationId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      logger.error("Error fetching gym locations:", error);
      throw error;
    }
    
    logger.info(`Successfully fetched ${data?.length || 0} gym locations`);
    return data || [];
  } catch (error) {
    logger.error(`Error getting all gym locations:`, error);
    return [];
  }
};

/**
 * Get a gym location by ID
 */
export const getLocationById = async (id: string, organizationId?: string): Promise<GymLocation | null> => {
  try {
    logger.info(`Getting gym location by ID: ${id}`);
    
    let query = supabase
      .from(TABLE_NAME)
      .select('*')
      .eq('id', id);
    
    // Apply organization scope if provided
    if (organizationId) {
      query = withOrganizationScope(query, organizationId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      logger.error(`Error fetching gym location by ID ${id}:`, error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      logger.warn(`No gym location found with ID: ${id}`);
      return null;
    }
    
    logger.info(`Successfully fetched gym location: ${data[0].name}`);
    return data[0];
  } catch (error) {
    logger.error(`Error getting gym location by ID ${id}:`, error);
    return null;
  }
};

/**
 * Create a new gym location
 * 
 * REQUIREMENTS:
 * - Explicitly whitelist only valid DB columns to prevent 400 errors from
 *   extra state properties (e.g. zipCode_valid from AddressFieldSet validation)
 * - Convert undefined/null optional fields to null for clean inserts
 */
export const createLocation = async (location: GymLocationInput): Promise<GymLocation | null> => {
  try {
    logger.info(`Creating new gym location: ${location.name}`);
    logger.debug('Location data being inserted:', JSON.stringify(location, null, 2));
    
    if (!location.organization_id) {
      logger.error("Cannot create location without organization_id");
      throw new Error("Organization ID is required to create a gym location");
    }

    // #1350: never silently default the timezone to Eastern. The create forms
    // infer it from the address (or require an explicit choice), so a missing
    // value here is a real bug — fail loudly instead of mis-tagging events.
    if (!location.timezone || location.timezone.trim() === '') {
      logger.error("Cannot create location without a timezone", { name: location.name });
      throw new Error("A timezone is required to create a gym location");
    }

    const timestamp = new Date().toISOString();
    
    // Explicitly whitelist only columns that exist in the gymlocations table.
    // Spreading the entire location object caused 400 errors when extra properties
    // leaked into state (e.g. zipCode_valid, zipCode_error from AddressFieldSet).
    const insertData = {
      organization_id: location.organization_id,
      name: location.name,
      address: location.address || null,
      address_line_2: location.address_line_2 || null,
      city: location.city || null,
      state: location.state || null,
      zip: location.zip || null,
      phone: location.phone || null,
      email: location.email || null,
      billing_contact_email: location.billing_contact_email || null,
      billing_contact_phone: location.billing_contact_phone || null,
      ...(location.reply_to_email ? { reply_to_email: location.reply_to_email } : {}),
      timezone: location.timezone,
      operating_hours: location.operating_hours || [],
      created_at: timestamp,
      updated_at: timestamp
    };
    
    logger.debug('Full insert payload:', JSON.stringify(insertData, null, 2));
    
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .insert(insertData)
      .select()
      .single();
    
    if (error) {
      logger.error("Supabase error creating gym location:", {
        message: error.message,
        hint: error.hint,
        code: error.code,
      });
      throw error;
    }
    
    logger.info(`Successfully created gym location with ID: ${data.id}`);

    // Auto-sync geofence for mobile check-in (fire-and-forget)
    syncLocationGeofence({
      name: data.name,
      address: data.address,
      city: data.city,
      state: data.state,
      zip: data.zip,
      organization_id: data.organization_id,
    });

    return data;
  } catch (error: any) {
    logger.error(`Error creating gym location:`, {
      message: error?.message,
      hint: error?.hint,
      code: error?.code,
      stack: error?.stack,
    });
    return null;
  }
};

/**
 * Update a gym location
 * 
 * REQUIREMENTS:
 * - Explicitly whitelist only valid DB columns (same as createLocation)
 * - Only include fields that are defined (not undefined) to avoid overwriting with null
 */
export const updateLocation = async (id: string, location: Partial<GymLocation>, organizationId?: string): Promise<boolean> => {
  try {
    logger.info(`Updating gym location with ID: ${id}`);

    // #1350: symmetric with createLocation — `undefined` means "not changing the
    // timezone", but an explicit blank must never wipe a location's zone (that
    // would mis-tag every event placed there). Fail loud instead.
    if (location.timezone !== undefined && (!location.timezone || location.timezone.trim() === '')) {
      logger.error('Refusing to update gym location with a blank timezone', { id });
      throw new Error('A timezone is required; cannot save a blank gym location timezone');
    }

    // If name is being changed, fetch the current name so the geofence sync
    // can find and update the existing record instead of creating a duplicate.
    let previousName: string | undefined;
    if (location.name !== undefined) {
      let nameQuery = supabase
        .from(TABLE_NAME)
        .select('name')
        .eq('id', id);
      if (organizationId) {
        nameQuery = withOrganizationScope(nameQuery, organizationId);
      }
      const { data: currentRecord } = await nameQuery.single();
      if (currentRecord && currentRecord.name !== location.name) {
        previousName = currentRecord.name;
      }
    }
    
    // Build update payload with only valid DB columns, skipping undefined fields
    const updatePayload: Record<string, any> = {
      updated_at: new Date().toISOString()
    };
    if (location.name !== undefined) updatePayload.name = location.name;
    if (location.address !== undefined) updatePayload.address = location.address || null;
    if (location.address_line_2 !== undefined) updatePayload.address_line_2 = location.address_line_2 || null;
    if (location.city !== undefined) updatePayload.city = location.city || null;
    if (location.state !== undefined) updatePayload.state = location.state || null;
    if (location.zip !== undefined) updatePayload.zip = location.zip || null;
    if (location.phone !== undefined) updatePayload.phone = location.phone || null;
    if (location.email !== undefined) updatePayload.email = location.email || null;
    if (location.billing_contact_email !== undefined) updatePayload.billing_contact_email = location.billing_contact_email || null;
    if (location.reply_to_email !== undefined) updatePayload.reply_to_email = location.reply_to_email || null;
    if (location.billing_contact_phone !== undefined) updatePayload.billing_contact_phone = location.billing_contact_phone || null;
    if (location.timezone !== undefined) updatePayload.timezone = location.timezone;
    if (location.operating_hours !== undefined) updatePayload.operating_hours = location.operating_hours;
    
    let query = supabase
      .from(TABLE_NAME)
      .update(updatePayload)
      .eq('id', id);
    
    // Apply organization scope if provided
    if (organizationId) {
      query = withOrganizationScope(query, organizationId);
    }
    
    // Use .select().single() to get the full updated record for geofence sync
    const { data: updatedRecord, error } = await query.select().single();
    
    if (error) {
      logger.error(`Error updating gym location ${id}:`, error);
      throw error;
    }
    
    logger.info(`Successfully updated gym location ${id}`);

    // Auto-sync geofence for mobile check-in (fire-and-forget)
    // Use the full updated record so we always have name + address,
    // even when only one field was changed in the partial update.
    if (updatedRecord && (location.address !== undefined || location.name !== undefined)) {
      syncLocationGeofence({
        name: updatedRecord.name,
        address: updatedRecord.address,
        city: updatedRecord.city,
        state: updatedRecord.state,
        zip: updatedRecord.zip,
        organization_id: updatedRecord.organization_id,
        previousName,
      });
    }

    return true;
  } catch (error) {
    logger.error(`Error updating gym location ${id}:`, error);
    return false;
  }
};

/**
 * Delete a gym location. Throws the database error so the caller can show it
 * (e.g. 23503 when class sign-ups or other records still use the location).
 */
export const deleteLocation = async (id: string, organizationId?: string): Promise<void> => {
  logger.info(`Deleting gym location with ID: ${id}`);

  let query = supabase
    .from(TABLE_NAME)
    .delete()
    .eq('id', id);

  // Apply organization scope if provided
  if (organizationId) {
    query = withOrganizationScope(query, organizationId);
  }

  const { data, error } = await query.select('id');

  if (error) {
    logger.error(`Error deleting gym location ${id}:`, error);
    throw error;
  }
  if (!data || data.length === 0) {
    throw new Error(`Gym location ${id} was not deleted`);
  }

  logger.info(`Successfully deleted gym location ${id}`);
};

/**
 * Get locations by organization ID
 */
export const getLocationsByOrganization = async (organizationId: string): Promise<GymLocation[]> => {
  try {
    logger.info(`Getting gym locations for organization: ${organizationId}`);
    
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select('*')
      .eq('organization_id', organizationId);
    
    if (error) {
      logger.error(`Error fetching locations for organization ${organizationId}:`, error);
      throw error;
    }
    
    logger.info(`Successfully fetched ${data?.length || 0} gym locations for organization ${organizationId}`);
    return data || [];
  } catch (error) {
    logger.error(`Error getting locations by organization ${organizationId}:`, error);
    return [];
  }
}; 